import React, { useEffect, useState } from "react";
import { CircleNotch, CaretDown } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { cn, truncMid, _decodeAssetName } from "@/lib/format";
import { Badge, CopyBtn, TxDetailRow } from "@/components/primitives";
import { AdaAmount } from "@/components/tx/AdaAmount";

// ---------- Addresses mode summary panel ----------
export const AddressSummaryPanel = ({ data, amountMode }) => {
  const [total, setTotal] = useState(null);
  const [account, setAccount] = useState(null);
  const [tokensOpen, setTokensOpen] = useState(false);
  const addr = data?.address;
  const stake = data?.stake_address;
  const used = data?._used; // true | false | null (unknown)
  const info = data?._info || {};
  // Only a chain-sourced stake address proves a real delegation/account
  // relationship; a credential decoded from the address bytes does not.
  const stakeFromChain = (info.stake_address_source || data?.stake_address_source) === "chain";

  useEffect(() => {
    if (!addr) return undefined;
    let cancelled = false;
    setTotal(null);
    setAccount(null);
    if (used === false) {
      // Valid but unused — zero activity; its list endpoints 404.
      setTotal({ tx_count: 0 });
      return () => { cancelled = true; };
    }
    http
      .get(`${API}/addresses/${addr}/tx_count`)
      .then((r) => { if (!cancelled) setTotal(r.data); })
      .catch((e) => {
        // used:null + 404 → unknown (never invent 0); genuine activity → leave pending
        if (!cancelled && used === null && e?.response?.status === 404) setTotal({ tx_count: null, unknown: true });
      });
    if (stake && stakeFromChain) {
      http
        .get(`${API}/accounts/${stake}`)
        .then((r) => { if (!cancelled) setAccount(r.data); })
        .catch((e) => console.warn("addr /accounts failed", e?.message));
    }
    return () => { cancelled = true; };
  }, [addr, stake, used, stakeFromChain]);

  if (!data) return null;
  const hasAmount = Array.isArray(data.amount);
  const lovelace = hasAmount ? (data.amount.find((a) => a.unit === "lovelace")?.quantity || "0") : "0";
  const balanceUnknown = used === null && !hasAmount;
  const tokens = hasAmount ? data.amount.filter((a) => a.unit !== "lovelace") : [];
  const tokenCount = tokens.length;
  const status = account ? (account.active ? { label: "Active", tone: "success" } : { label: "Not Active", tone: "warn" }) : null;
  const credLabel = (c) => (c ? `${c.kind === "script" ? "script" : "key"} · ${truncMid(c.hash, 10, 6)}` : null);

  return (
    <div className="space-y-6" data-testid="address-summary-panel">
      <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md box-glow">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70 tracing-beam pointer-events-none" />
        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-primary/15">
          {/* LEFT — address summary */}
          <div className="px-6 py-2">
            <TxDetailRow label="Address" testid="addr-row-bech32">
              <span className="inline-flex items-center gap-2 justify-end flex-wrap">
                <span className="text-primary text-glow break-all text-left font-mono text-[12px]">
                  {data.address}
                </span>
                <CopyBtn value={data.address} label="address" />
              </span>
            </TxDetailRow>
            <TxDetailRow label="Type" testid="addr-row-type">
              <span className="inline-flex items-center gap-2">
                <Badge tone="info">{data.type}</Badge>
                {data.script ? <Badge tone="warn">script</Badge> : null}
              </span>
            </TxDetailRow>
            {(info.network || info.era) ? (
              <TxDetailRow label="Network / Era" testid="addr-row-network">
                <span className="inline-flex items-center gap-2">
                  {info.network ? <Badge tone="info">{info.network}</Badge> : null}
                  {info.era ? <Badge tone="info">{info.era}</Badge> : null}
                </span>
              </TxDetailRow>
            ) : null}
            {info.payment_credential ? (
              <TxDetailRow label="Payment Credential" testid="addr-row-payment-cred">
                <span className="inline-flex items-center gap-2 justify-end flex-wrap">
                  <span className="font-mono text-[12px] text-foreground/90 break-all">{credLabel(info.payment_credential)}</span>
                  <CopyBtn value={info.payment_credential.hash} label="payment-cred" />
                </span>
              </TxDetailRow>
            ) : null}
            <TxDetailRow label="Balance" testid="addr-row-balance">
              {balanceUnknown ? (
                <span className="text-muted-foreground italic">Unknown</span>
              ) : (
                <AdaAmount lovelaceStr={lovelace} />
              )}
            </TxDetailRow>
            <TxDetailRow label="Transaction Count" testid="addr-row-tx-count">
              {total ? (
                total.unknown ? (
                  <span className="text-muted-foreground italic">Unknown</span>
                ) : (
                  <span className="text-foreground text-[15px]">
                    {Number(total.tx_count ?? 0).toLocaleString("en-US")}
                  </span>
                )
              ) : (
                <CircleNotch size={12} className="animate-spin text-muted-foreground inline-block" />
              )}
            </TxDetailRow>
            <TxDetailRow label="Tokens" testid="addr-row-tokens">
              {tokenCount > 0 ? (
                <button
                  type="button"
                  data-testid="tokens-dropdown-toggle"
                  onClick={() => setTokensOpen((o) => !o)}
                  className="inline-flex items-center gap-1.5 text-foreground text-[15px] hover:text-[#22d3ee] transition-colors"
                >
                  {tokenCount} {tokenCount === 1 ? "token" : "tokens"}
                  <CaretDown size={12} className={cn("transition-transform", tokensOpen && "rotate-180")} />
                </button>
              ) : (
                <span className="text-foreground text-[15px]">0 tokens</span>
              )}
            </TxDetailRow>
            {tokensOpen && tokenCount > 0 ? (
              <div data-testid="tokens-dropdown-list" className="mb-3 -mt-1 max-h-[260px] overflow-y-auto rounded-xl border border-primary/15 bg-primary/[0.03] divide-y divide-primary/10">
                {tokens.map((t, i) => {
                  const { name } = _decodeAssetName(t.unit);
                  return (
                    <div key={t.unit} data-testid={`token-item-${i}`} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <CopyBtn value={t.unit} label="asset" />
                        <span className="font-mono text-[12px] text-[#22d3ee] truncate" title={t.unit}>
                          {name || truncMid(t.unit, 10, 6)}
                        </span>
                      </span>
                      <span className="font-mono text-[12px] tabnum text-foreground/90 shrink-0">
                        {Number(t.quantity).toLocaleString("en-US")}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {used === false ? (
              <div data-testid="addr-unused-note" className="mt-3 rounded-xl border border-[#22d3ee]/25 bg-[#22d3ee]/[0.05] px-4 py-3">
                <div className="text-[12px] text-[#22d3ee] font-medium">No recorded on-chain activity</div>
                <div className="text-[11px] text-muted-foreground mono mt-1">Balance 0 ₳ · Transactions 0 · UTxOs 0</div>
                <div className="text-[10px] text-muted-foreground/70 mt-1">This address is valid but has never appeared on-chain.</div>
              </div>
            ) : null}
          </div>
          {/* RIGHT — stake key + delegation */}
          <div className="px-6 py-2">
            {data.stake_address && stakeFromChain && used !== false ? (
              <>
                <TxDetailRow label="Controlled Stake Key" testid="addr-row-stake">
                  <span className="inline-flex items-center gap-2 justify-end flex-wrap">
                    <span className="text-primary text-glow break-all text-left font-mono text-[12px]">
                      {data.stake_address}
                    </span>
                    <CopyBtn value={data.stake_address} label="stake" />
                  </span>
                </TxDetailRow>
                <TxDetailRow label="Status" testid="addr-row-stake-status">
                  {account ? (
                    <Badge tone={status.tone}>{status.label}</Badge>
                  ) : (
                    <CircleNotch size={12} className="animate-spin text-muted-foreground inline-block" />
                  )}
                </TxDetailRow>
                <TxDetailRow label="Controlled Total Stake" testid="addr-row-total-stake">
                  {account ? (
                    <AdaAmount lovelaceStr={account.controlled_amount} />
                  ) : (
                    <CircleNotch size={12} className="animate-spin text-muted-foreground inline-block" />
                  )}
                </TxDetailRow>
                <TxDetailRow label="Rewards Withdrawn" testid="addr-row-rewards-withdrawn">
                  {account ? (
                    <AdaAmount lovelaceStr={account.withdrawals_sum} />
                  ) : (
                    <CircleNotch size={12} className="animate-spin text-muted-foreground inline-block" />
                  )}
                </TxDetailRow>
                <TxDetailRow label="Rewards Available" testid="addr-row-rewards-available">
                  {account ? (
                    <AdaAmount lovelaceStr={account.withdrawable_amount} />
                  ) : (
                    <CircleNotch size={12} className="animate-spin text-muted-foreground inline-block" />
                  )}
                </TxDetailRow>
                <TxDetailRow label="Delegated To" testid="addr-row-delegated">
                  {!account ? (
                    <CircleNotch size={12} className="animate-spin text-muted-foreground inline-block" />
                  ) : account.pool_id ? (
                    <span className="inline-flex items-center gap-2 justify-end flex-wrap">
                      <span className="text-primary text-glow font-mono text-[12px] break-all">
                        {truncMid(account.pool_id, 16, 8)}
                      </span>
                      <CopyBtn value={account.pool_id} label="pool" />
                    </span>
                  ) : (
                    <span className="text-muted-foreground italic">Not delegated to any pool</span>
                  )}
                </TxDetailRow>
              </>
            ) : data.stake_address ? (
              <div className="px-6 py-6" data-testid="addr-derived-stake">
                <TxDetailRow label="Stake Credential" testid="addr-row-stake">
                  <span className="inline-flex items-center gap-2 justify-end flex-wrap">
                    <span className="text-primary text-glow break-all text-left font-mono text-[12px]">
                      {data.stake_address}
                    </span>
                    <CopyBtn value={data.stake_address} label="stake" />
                  </span>
                </TxDetailRow>
                <div className="mt-3 text-[11px] text-muted-foreground/80 leading-relaxed">
                  Decoded from the address's stake credential. This does not imply any on-chain delegation or account activity.
                </div>
              </div>
            ) : (
              <div className="px-6 py-6 flex flex-col items-center justify-center h-full" data-testid="wallet-snapshot">
                <h3 className="font-heading text-[14px] md:text-[15px] text-[#22d3ee] text-glow-cyan mb-2">Wallet Snapshot</h3>
                <div className="relative w-[200px] h-[200px] my-2">
                  {/* static concentric rings */}
                  <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full">
                    <circle cx="100" cy="100" r="92" fill="none" stroke="hsl(264 100% 68% / 0.12)" strokeWidth="1.5" />
                    <circle cx="100" cy="100" r="72" fill="none" stroke="hsl(188 90% 52% / 0.12)" strokeWidth="1.5" />
                    <circle cx="100" cy="100" r="52" fill="none" stroke="hsl(264 100% 68% / 0.16)" strokeWidth="1.5" />
                    <circle cx="100" cy="100" r="92" fill="none" stroke="hsl(264 100% 68% / 0.06)" strokeWidth="14" />
                  </svg>
                  {/* outer rotating arc (violet) */}
                  <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" style={{ animation: "spin 16s linear infinite" }}>
                    <circle cx="100" cy="100" r="92" fill="none" stroke="hsl(264 100% 68%)" strokeWidth="3" strokeLinecap="round" strokeDasharray="80 498" />
                  </svg>
                  {/* mid rotating arc (cyan, reverse) */}
                  <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" style={{ animation: "spin 11s linear infinite reverse" }}>
                    <circle cx="100" cy="100" r="72" fill="none" stroke="#22d3ee" strokeWidth="3" strokeLinecap="round" strokeDasharray="55 398" />
                  </svg>
                  {/* inner rotating arc (orange) */}
                  <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" style={{ animation: "spin 22s linear infinite" }}>
                    <circle cx="100" cy="100" r="52" fill="none" stroke="#fb923c" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="30 297" />
                  </svg>
                  {/* center balance */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground">Balance</span>
                    <div className="mt-1 scale-90"><AdaAmount lovelaceStr={lovelace} /></div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 w-full mt-3">
                  <div className="rounded-xl border border-primary/15 bg-primary/[0.04] px-2 py-2 text-center">
                    <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Txns</div>
                    <div className="font-heading text-[13px] tabnum text-[#22d3ee] mt-0.5">
                      {total ? Number(total.tx_count ?? 0).toLocaleString("en-US") : "…"}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="snapshot-tokens-toggle"
                    onClick={() => tokenCount > 0 && setTokensOpen((o) => !o)}
                    className={cn(
                      "rounded-xl border border-primary/15 bg-primary/[0.04] px-2 py-2 text-center transition-colors",
                      tokenCount > 0 ? "hover:border-[#22d3ee]/50 cursor-pointer" : "cursor-default",
                    )}
                  >
                    <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground inline-flex items-center gap-1 justify-center">
                      Tokens
                      {tokenCount > 0 ? <CaretDown size={10} className={cn("transition-transform", tokensOpen && "rotate-180")} /> : null}
                    </div>
                    <div className="font-heading text-[13px] tabnum text-[#22d3ee] mt-0.5">{tokenCount}</div>
                  </button>
                  <div className="rounded-xl border border-primary/15 bg-primary/[0.04] px-2 py-2 text-center">
                    <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Type</div>
                    <div className="font-heading text-[13px] text-[#22d3ee] mt-0.5">{data.type}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
